import type { LocalizedString } from "@repo/shared/types";
/**
 * Placeholder Transformer Service
 *
 * Transforms PDF template placeholders between:
 * - Storage format (ID-based): {{Client.clx123abc}}, {{Flow.cly456def}}, {{Quiz.<uuid>}}
 * - Display format (localized names): {{Client.Company Size}}, {{Flow.Risk Score}},
 *   {{Quiz.Listed Equities}}
 *
 * Standard fields like {{Client.Name}}, {{Client.Email}} remain unchanged.
 */

import { ADVISOR_NOTE_FIELD_PREFIX, advisorNotePageDisplayNames } from "@repo/shared/types";

// Standard client fields that use name-based placeholders (not IDs)
const STANDARD_CLIENT_FIELDS = new Set([
  "name",
  "email",
  "mobilephone",
  "mobile phone",
  "ssn",
  "orgid",
  "org id",
  "country",
  "type",
  "owner",
]);

// Standard flow fields that use name-based placeholders (the same four the
// PDF renderer resolves before it looks at flow field ids)
const STANDARD_FLOW_FIELDS = new Set([
  "name",
  "cancelreason",
  "expectingfinancialchanges",
  "expectingfinancialchangesnote",
]);

export type FieldMapEntry = {
  id: string;
  name: string; // Display name in user's language
  nameI18n?: LocalizedString; // All translations (for custom fields)
};

export type FieldMap = {
  clientFields: Map<string, FieldMapEntry>; // id -> entry
  flowFields: Map<string, FieldMapEntry>; // id -> entry
  // QL-13 — quizzes, for the {{Quiz.*}} namespace
  quizzes: Map<string, FieldMapEntry>; // id -> entry
  // Reverse maps for display-to-storage conversion
  clientFieldsByName: Map<string, FieldMapEntry>; // lowercase name -> entry
  flowFieldsByName: Map<string, FieldMapEntry>; // lowercase name -> entry
  quizzesByName: Map<string, FieldMapEntry>; // lowercase name -> entry
  // QL-414 — flow pages that take advisor notes, for the {{AdvisorNote.*}} namespace
  advisorNotePages: Map<string, FieldMapEntry>; // id -> entry
  advisorNotePagesByName: Map<string, FieldMapEntry>; // lowercase name -> entry
  // QL-610 — lowercase flow field names that more than one field carries, or
  // that a standard flow placeholder already uses
  ambiguousFlowFieldNames: Set<string>;
};

/**
 * Check if a field reference is a stored ID (cuid or UUID) rather than a name
 * - cuid: starts with 'c', 20+ chars alphanumeric
 * - UUID: 8-4-4-4-12 hex with dashes (from crypto.randomUUID())
 */
export function isFieldId(ref: string): boolean {
  if (ref.length >= 20 && ref.startsWith("c") && /^c[a-z0-9]+$/.test(ref)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(ref)) return true;
  return false;
}

/**
 * QL-414 — whether a reference is an `AdvisorNote.*` field. Case-insensitive,
 * like the editor's validation and the PDF resolver: a hand-typed
 * `advisornote.Classification` must be stored by id like any other.
 */
export function hasAdvisorNotePrefix(ref: string): boolean {
  return ref.toLowerCase().startsWith(ADVISOR_NOTE_FIELD_PREFIX.toLowerCase());
}

/**
 * QL-13 — a quiz reference is `<quiz>`, `<quiz>.Override` or `<quiz>.External`.
 * Only the first part names the quiz; the suffix rides along untouched through
 * both directions of the transform, so a rename never loses it.
 */
export const QUIZ_OVERRIDE_SUFFIX = ".Override";
export const QUIZ_EXTERNAL_SUFFIX = ".External";

const QUIZ_REF_SUFFIXES = [QUIZ_OVERRIDE_SUFFIX, QUIZ_EXTERNAL_SUFFIX];

export function splitQuizRef(quizRef: string): { ref: string; suffix: string } {
  const suffix = QUIZ_REF_SUFFIXES.find((candidate) =>
    quizRef.toLowerCase().endsWith(candidate.toLowerCase()),
  );
  return suffix ? { ref: quizRef.slice(0, -suffix.length), suffix } : { ref: quizRef, suffix: "" };
}

/**
 * Build a field map from custom fields and flow fields
 */
export function buildFieldMap(
  customFields: Array<{ id: string; name?: string; nameI18n?: LocalizedString | null }>,
  flowFields: Array<{ id: string; name: string }>,
  locale: string,
  quizzes: Array<{ id: string; name: string }> = [],
  advisorNotePages: Array<{
    id: string;
    name: string;
    nameI18n?: Record<string, string> | null;
  }> = [],
): FieldMap {
  const clientFields = new Map<string, FieldMapEntry>();
  const clientFieldsByName = new Map<string, FieldMapEntry>();
  const flowFieldsMap = new Map<string, FieldMapEntry>();
  const flowFieldsByName = new Map<string, FieldMapEntry>();
  const quizzesMap = new Map<string, FieldMapEntry>();
  const quizzesByName = new Map<string, FieldMapEntry>();

  // Process custom fields (client fields with i18n)
  for (const field of customFields) {
    const i18n = field.nameI18n as Record<string, string> | undefined;
    // Get display name: prefer user's locale, fall back to any available value
    const displayName = i18n?.[locale] || field.name || Object.values(i18n || {})[0] || field.id;

    const entry: FieldMapEntry = {
      id: field.id,
      name: displayName,
      nameI18n: field.nameI18n || undefined,
    };

    clientFields.set(field.id, entry);
    clientFieldsByName.set(displayName.toLowerCase(), entry);

    // Also index by all translated names for reverse lookup
    if (i18n) {
      for (const translatedName of Object.values(i18n)) {
        if (translatedName) {
          clientFieldsByName.set(translatedName.toLowerCase(), entry);
        }
      }
    }
  }

  // Process flow fields (no i18n, just name). Nothing stops two fields of a
  // flow sharing a name (Add Field, or duplicating a field), so a shared name
  // resolves to NOTHING, as for quizzes below: picking the last match would
  // silently re-point `{{Flow.<id>}}` at the other field (QL-610). A field
  // named like a standard placeholder (`Name`, `CancelReason`, …) is the same
  // case: `{{Flow.Name}}` already means the flow's own name.
  const ambiguousFlowFieldNames = new Set<string>();
  for (const field of flowFields) {
    const entry: FieldMapEntry = {
      id: field.id,
      name: field.name,
    };
    const nameKey = field.name.toLowerCase();

    flowFieldsMap.set(field.id, entry);
    if (flowFieldsByName.has(nameKey) || STANDARD_FLOW_FIELDS.has(nameKey)) {
      ambiguousFlowFieldNames.add(nameKey);
    }
    flowFieldsByName.set(nameKey, entry);
  }
  for (const nameKey of ambiguousFlowFieldNames) flowFieldsByName.delete(nameKey);

  // QL-13 — quiz names are plain strings (no i18n; that is the ticket's "no
  // multi language"), so one entry each way is enough. The API already
  // disambiguates repeated titles, but a name that still collides must resolve
  // to NOTHING rather than to whichever quiz was listed last: binding
  // `{{Quiz.<name>}}` to the wrong id would render a different quiz's result on
  // a compliance document with no visible sign anything was wrong.
  const ambiguousQuizNames = new Set<string>();
  for (const quizEntry of quizzes) {
    const entry: FieldMapEntry = { id: quizEntry.id, name: quizEntry.name };
    const nameKey = quizEntry.name.toLowerCase();
    quizzesMap.set(quizEntry.id, entry);
    if (quizzesByName.has(nameKey)) ambiguousQuizNames.add(nameKey);
    quizzesByName.set(nameKey, entry);
  }
  for (const nameKey of ambiguousQuizNames) quizzesByName.delete(nameKey);

  // QL-414 — advisor note pages, by id and by the name the editor shows. The
  // shown names are unique (`advisorNotePageDisplayNames`), so a name binds to
  // exactly one page. As with quizzes, a name that still collides must resolve
  // to NOTHING rather than to whichever page was listed last: that would
  // rewrite the stored id and print another page's note.
  const advisorNotePagesMap = new Map<string, FieldMapEntry>();
  const advisorNotePagesByName = new Map<string, FieldMapEntry>();
  const ambiguousAdvisorNotePageNames = new Set<string>();
  for (const [id, name] of advisorNotePageDisplayNames(advisorNotePages, locale)) {
    const entry: FieldMapEntry = { id, name };
    const nameKey = name.toLowerCase();
    advisorNotePagesMap.set(id, entry);
    if (advisorNotePagesByName.has(nameKey)) ambiguousAdvisorNotePageNames.add(nameKey);
    advisorNotePagesByName.set(nameKey, entry);
  }
  for (const nameKey of ambiguousAdvisorNotePageNames) advisorNotePagesByName.delete(nameKey);

  return {
    clientFields,
    flowFields: flowFieldsMap,
    quizzes: quizzesMap,
    clientFieldsByName,
    flowFieldsByName,
    quizzesByName,
    advisorNotePages: advisorNotePagesMap,
    advisorNotePagesByName,
    ambiguousFlowFieldNames,
  };
}

/**
 * QL-610 — the `Flow.*` reference the field picker inserts. A field whose name
 * another field or a standard placeholder shares is inserted, and shown, by
 * id: its name would not resolve back to it.
 */
export function flowFieldDisplayRef(field: { id: string; name: string }, fieldMap: FieldMap) {
  return fieldMap.ambiguousFlowFieldNames.has(field.name.toLowerCase())
    ? `Flow.${field.id}`
    : `Flow.${field.name}`;
}

/**
 * Transform content from storage format (IDs) to display format (localized names)
 *
 * {{Client.clx123abc}} -> {{Client.Company Size}}
 * {{Flow.cly456def}} -> {{Flow.Risk Score}}
 * {{Client.Name}} -> {{Client.Name}} (unchanged - standard field)
 */
export function transformToDisplay(content: string, fieldMap: FieldMap): string {
  if (!content) return content;

  // Match all placeholders: {{...}}
  return content.replace(/\{\{([^}]+)\}\}/g, (match, placeholder) => {
    const trimmed = placeholder.trim();

    // Handle Client.* placeholders
    if (trimmed.startsWith("Client.")) {
      const fieldRef = trimmed.substring(7); // Remove "Client."

      // Check if it's a standard field (name-based) - leave unchanged
      if (STANDARD_CLIENT_FIELDS.has(fieldRef.toLowerCase())) {
        return match;
      }

      // Check if it's an ID - transform to display name
      if (isFieldId(fieldRef)) {
        const entry = fieldMap.clientFields.get(fieldRef);
        if (entry) {
          return `{{Client.${entry.name}}}`;
        }
      }

      // Not found or already a name - return unchanged
      return match;
    }

    // Handle Flow.* placeholders
    if (trimmed.startsWith("Flow.")) {
      const fieldRef = trimmed.substring(5); // Remove "Flow."

      // Check if it's a standard flow field - leave unchanged
      if (STANDARD_FLOW_FIELDS.has(fieldRef.toLowerCase())) {
        return match;
      }

      // Check if it's an ID - transform to display name, unless another field
      // shares that name: then the id stays, so the round trip keeps it
      if (isFieldId(fieldRef)) {
        const entry = fieldMap.flowFields.get(fieldRef);
        if (entry && !fieldMap.ambiguousFlowFieldNames.has(entry.name.toLowerCase())) {
          return `{{Flow.${entry.name}}}`;
        }
      }

      // Not found or already a name - return unchanged
      return match;
    }

    // QL-13 — {{Quiz.<uuid>}} -> {{Quiz.<Quiz Name>}}, and the same for the
    // `.Override` and `.External` variants that disclose how a pass was reached.
    if (trimmed.startsWith("Quiz.")) {
      const { ref: quizRef, suffix } = splitQuizRef(trimmed.substring(5));
      if (isFieldId(quizRef)) {
        const entry = fieldMap.quizzes.get(quizRef);
        if (entry) return `{{Quiz.${entry.name}${suffix}}}`;
      }
      return match;
    }

    // QL-414 — {{AdvisorNote.<page id>}} -> {{AdvisorNote.<Page name>}}
    if (hasAdvisorNotePrefix(trimmed)) {
      const entry = fieldMap.advisorNotePages.get(
        trimmed.substring(ADVISOR_NOTE_FIELD_PREFIX.length),
      );
      return entry ? `{{${ADVISOR_NOTE_FIELD_PREFIX}${entry.name}}}` : match;
    }

    // System placeholders and others - leave unchanged
    return match;
  });
}

/**
 * Transform content from display format (localized names) to storage format (IDs)
 *
 * {{Client.Company Size}} -> {{Client.clx123abc}}
 * {{Flow.Risk Score}} -> {{Flow.cly456def}}
 * {{Client.Name}} -> {{Client.Name}} (unchanged - standard field)
 */
export function transformToStorage(content: string, fieldMap: FieldMap): string {
  if (!content) return content;

  // Match all placeholders: {{...}}
  return content.replace(/\{\{([^}]+)\}\}/g, (match, placeholder) => {
    const trimmed = placeholder.trim();

    // Handle Client.* placeholders
    if (trimmed.startsWith("Client.")) {
      const fieldRef = trimmed.substring(7); // Remove "Client."

      // Check if it's a standard field - leave unchanged
      if (STANDARD_CLIENT_FIELDS.has(fieldRef.toLowerCase())) {
        return match;
      }

      // Check if it's already an ID - leave unchanged
      if (isFieldId(fieldRef)) {
        return match;
      }

      // It's a name - look up the ID
      const entry = fieldMap.clientFieldsByName.get(fieldRef.toLowerCase());
      if (entry) {
        return `{{Client.${entry.id}}}`;
      }

      // Not found - return unchanged (might be invalid)
      return match;
    }

    // Handle Flow.* placeholders
    if (trimmed.startsWith("Flow.")) {
      const fieldRef = trimmed.substring(5); // Remove "Flow."

      // Check if it's a standard flow field - leave unchanged
      if (STANDARD_FLOW_FIELDS.has(fieldRef.toLowerCase())) {
        return match;
      }

      // Check if it's already an ID - leave unchanged
      if (isFieldId(fieldRef)) {
        return match;
      }

      // It's a name - look up the ID
      const entry = fieldMap.flowFieldsByName.get(fieldRef.toLowerCase());
      if (entry) {
        return `{{Flow.${entry.id}}}`;
      }

      // Not found - return unchanged (might be invalid)
      return match;
    }

    // QL-13 — {{Quiz.<Quiz Name>}} -> {{Quiz.<uuid>}}, suffixes included.
    if (trimmed.startsWith("Quiz.")) {
      const { ref: quizRef, suffix } = splitQuizRef(trimmed.substring(5));
      if (isFieldId(quizRef)) return match;
      const entry = fieldMap.quizzesByName.get(quizRef.toLowerCase());
      if (entry) return `{{Quiz.${entry.id}${suffix}}}`;
      return match;
    }

    // QL-414 — {{AdvisorNote.<Page name>}} -> {{AdvisorNote.<page id>}}
    if (hasAdvisorNotePrefix(trimmed)) {
      const pageRef = trimmed.substring(ADVISOR_NOTE_FIELD_PREFIX.length);
      if (fieldMap.advisorNotePages.has(pageRef)) return match;
      const entry = fieldMap.advisorNotePagesByName.get(pageRef.toLowerCase());
      return entry ? `{{${ADVISOR_NOTE_FIELD_PREFIX}${entry.id}}}` : match;
    }

    // System placeholders and others - leave unchanged
    return match;
  });
}
